import type { RecommendationCatalogEntry } from "@/api/mock-data/agent-estate/seed";
import type {
  AgentBusinessUnit,
  AgentPurpose,
  AgentTechnology,
} from "@/api/temp/AgentEstateService";

import { CUSTOMER_SUPPORT_DATA_ACCESS } from "./intentPolicies";
import type { DemoDomain, DemoResourceKey } from "./resourceMappings";

export { DEMO_RESOURCE_BINDINGS } from "./resourceMappings";
export type DemoScenarioKey = "customer-support" | "churn-analytics";

export const DEMO_SERVER_RESOURCES: Record<string, DemoResourceKey> = {
  "mcp-kb-private-banking": "support",
  "mcp-m365-rm-workspace": "support",
  "mcp-sharepoint-kyc": "employee",
  "mcp-kb-hr-benefits": "employee",
  "mcp-snowflake-markets": "finance",
  "mcp-snowflake-risk": "risk",
  "mcp-snowflake-warehouse": "risk",
  "mcp-databricks-risk-models": "operations",
  "mcp-databricks-trading-systems": "operations",
  "mcp-kb-research": "risk",
  "mcp-sharepoint-support": "supportWarehouse",
  "mcp-servicenow-support": "supportWarehouse",
  "mcp-salesforce-support": "customerPlatform",
};

/**
 * Named agents beyond the two scenario leads, keyed `${businessUnit}:${index}`
 * into the generated slots. Each takes over that slot's identity: the key
 * becomes its id suffix and service account, so it is addressable like a
 * scenario agent without carrying a scenario's recommendation of its own.
 */
export interface DemoAgentProfile {
  key: string;
  name: string;
  mission: string;
  purpose: AgentPurpose;
  technology: AgentTechnology;
  serverIds: string[];
  allowedDomains: DemoDomain[];
}

const supportProfile = (
  key: string,
  name: string,
  mission: string,
  serverIds: string[],
): DemoAgentProfile => ({
  key,
  name,
  mission,
  purpose: "CUSTOMER_SUPPORT",
  technology: serverIds.includes("mcp-salesforce-support")
    ? "Salesforce"
    : "ServiceNow",
  serverIds,
  allowedDomains: ["CUSTOMER_SUPPORT"],
});

export const DEMO_AGENT_PROFILES: Record<string, DemoAgentProfile> = {
  "CUSTOMER_SUPPORT:1": supportProfile(
    "service-desk",
    "Service Desk Agent",
    "Triage incoming service requests and route them to the right support queue using ticket and knowledge-base context.",
    ["mcp-sharepoint-support", "mcp-servicenow-support"],
  ),
  "CUSTOMER_SUPPORT:2": supportProfile(
    "ticket-resolution",
    "Ticket Resolution Agent",
    "Draft resolutions for open service tickets from case history and approved support answers.",
    ["mcp-servicenow-support"],
  ),
  "CUSTOMER_SUPPORT:3": supportProfile(
    "customer-insights",
    "Customer Insights Agent",
    "Summarize customer account and case activity so support teams see the full service history.",
    ["mcp-salesforce-support"],
  ),
  "ANALYTICS:2": {
    ...supportProfile(
      "client-retention-insights",
      "Client Retention Insights Agent",
      "Surface retention signals from customer accounts and support interactions for the client service team.",
      ["mcp-salesforce-support"],
    ),
    purpose: "ANALYTICS",
  },
};

interface DemoScenario {
  name: string;
  mission: string;
  context: string;
  businessUnit: AgentBusinessUnit;
  purpose: AgentPurpose;
  technology: AgentTechnology;
  serverIds: string[];
  allowedDomains: DemoDomain[];
  deniedDomains: DemoDomain[];
  expected: Array<{ operation: string; decision: "ALLOW" | "DENY" | "REDACT" }>;
  recommendation: RecommendationCatalogEntry;
}

export const DEMO_SCENARIOS: Record<DemoScenarioKey, DemoScenario> = {
  "customer-support": {
    name: "Customer Support Agent",
    mission:
      "Assist customer support teams in resolving customer inquiries and service tickets using approved customer-support information.",
    context:
      "Customer Operations identity; resolving support cases requires approved contact context, not employee records, financial reporting or trading activity.",
    businessUnit: "CUSTOMER_SUPPORT",
    purpose: "CUSTOMER_SUPPORT",
    technology: "ServiceNow",
    serverIds: ["mcp-sharepoint-support", "mcp-servicenow-support"],
    allowedDomains: ["CUSTOMER_SUPPORT"],
    deniedDomains: ["FINANCE", "TRADING", "EMPLOYEE_HR"],
    expected: [
      {
        operation: "Read approved support tickets and case history",
        decision: "ALLOW",
      },
      {
        operation: "Read Finance or internal financial information",
        decision: "DENY",
      },
      { operation: "Read Trading orders or history", decision: "DENY" },
      {
        operation: "Read Employee/HR, payroll or compensation",
        decision: "DENY",
      },
    ],
    recommendation: {
      id: CUSTOMER_SUPPORT_DATA_ACCESS.id,
      demoScenario: "customer-support",
      name: CUSTOMER_SUPPORT_DATA_ACCESS.name,
      outcome:
        "Allow customer support and ticket data while restricting Finance, Trading and Employee Data.",
      whyRecommended:
        "The agent's Customer Operations identity and support mission require tickets, case history and approved contact context. Finance, Trading and Employee Data are outside that purpose.",
      controls: [
        ...CUSTOMER_SUPPORT_DATA_ACCESS.allow.map(label => ({
          sense: "ALLOW" as const,
          label,
        })),
        { sense: "BLOCK", label: "Access to Finance data" },
        { sense: "BLOCK", label: "Access to Trading data" },
        { sense: "BLOCK", label: "Access to Employee Data" },
      ],
      category: "DATA_ACCESS_CONTROLS",
      technology: "ServiceNow",
      strength: "STRONGLY_RECOMMENDED",
      frameworks: [],
      enforcement: {
        resource: "Approved customer service resources",
        toolGroups: ["Support resource scope"],
        allowed: ["searchIncidents", "getCaseHistory", "searchDocuments"],
        blocked: ["Finance", "Trading", "Employee Data"],
        enforcementLayer:
          "MCP server and tool policies, and data-source access controls",
      },
      selector: {
        purposes: ["CUSTOMER_SUPPORT", "ANALYTICS"],
        technologies: ["SharePoint", "ServiceNow", "Salesforce"],
        agentKeys: CUSTOMER_SUPPORT_DATA_ACCESS.agentKeys,
      },
    },
  },
  "churn-analytics": {
    name: "Churn Analytics Agent",
    mission:
      "Analyze enterprise and customer trends to identify churn signals and generate aggregate retention insights without exposing raw customer-identifying information.",
    context:
      "Risk & Quant Research identity; approved Finance, Trading and operational analytics are read-only. Customer-identifying fields require masking or redaction.",
    businessUnit: "ANALYTICS",
    purpose: "ANALYTICS",
    technology: "Snowflake",
    serverIds: [
      "mcp-snowflake-risk",
      "mcp-snowflake-markets",
      "mcp-databricks-risk-models",
    ],
    allowedDomains: [
      "FINANCE",
      "TRADING",
      "RISK_COMPLIANCE",
      "CUSTOMER_SENSITIVE",
    ],
    deniedDomains: ["EMPLOYEE_HR"],
    expected: [
      {
        operation: "Read approved aggregate enterprise analytics",
        decision: "ALLOW",
      },
      {
        operation: "Insert, update, delete, alter or drop data",
        decision: "DENY",
      },
      { operation: "Return customer-identifying fields", decision: "REDACT" },
    ],
    recommendation: {
      id: "demo-churn-readonly-redaction",
      demoScenario: "churn-analytics",
      name: "Read-Only Enterprise Analytics with Customer Data Redaction",
      outcome:
        "Allow approved analytical reads, deny writes and destructive actions, and require customer PII redaction.",
      whyRecommended:
        "This research identity needs broad analytical context to identify churn. Its mission requires aggregate insights rather than changing enterprise data or exposing sensitive customer fields.",
      controls: [
        {
          sense: "ALLOW",
          label:
            "Read approved Finance, Trading, operational and risk analytics",
        },
        {
          sense: "ALLOW",
          label:
            "Return aggregate insights with customer-identifying fields masked or redacted",
        },
        { sense: "BLOCK", label: "Insert, update, delete, alter or drop data" },
        {
          sense: "BLOCK",
          label:
            "Return raw customer PII or unmasked sensitive customer fields",
        },
      ],
      category: "DATA_ACCESS_CONTROLS",
      technology: "Snowflake",
      strength: "STRONGLY_RECOMMENDED",
      frameworks: [],
      enforcement: {
        resource: "Approved enterprise analytical resources",
        toolGroups: ["Read-only analytics", "Customer data redaction"],
        allowed: ["run_query (read-only)", "query_table (read-only)"],
        blocked: [
          "INSERT",
          "UPDATE",
          "DELETE",
          "ALTER",
          "DROP",
          "Unmasked customer PII",
        ],
        enforcementLayer:
          "Expected read-only access and redaction",
      },
      selector: {
        purposes: ["ANALYTICS"],
        technologies: ["Snowflake", "Databricks"],
      },
    },
  },
};

export const DEMO_UNIT_COUNTS: Record<AgentBusinessUnit, number> = {
  HR: 18,
  CUSTOMER_SUPPORT: 26,
  FINANCE: 30,
  ENGINEERING: 20,
  ANALYTICS: 24,
};
export const DEMO_AGENT_ROLES: Record<AgentBusinessUnit, string[]> = {
  HR: [
    "Employee Service Agent",
    "HR Benefits Agent",
    "Employee Onboarding Agent",
  ],
  CUSTOMER_SUPPORT: [
    "Customer Support Agent",
    "Client Service Agent",
    "Client Onboarding Agent",
    "KYC Review Agent",
  ],
  FINANCE: [
    "Trade Operations Agent",
    "Trade Reconciliation Agent",
    "Finance Reporting Agent",
    "Treasury Analytics Agent",
    "Portfolio Analytics Agent",
  ],
  ENGINEERING: [
    "Operations Exception Agent",
    "Trading Platform Agent",
    "Market Data Operations Agent",
  ],
  ANALYTICS: [
    "Churn Analytics Agent",
    "Market Research Agent",
    "Risk Analytics Agent",
    "Credit Risk Agent",
    "Compliance Review Agent",
    "AML Investigation Agent",
    "Regulatory Reporting Agent",
    "Investment Research Agent",
  ],
};

const SUPPORT_AGENT_KEYS = [
  "customer-support",
  "service-desk",
  "ticket-resolution",
  "customer-insights",
];

/**
 * Further recommendations for the support scenario. Advisory - they widen the
 * Recommended tab without becoming coverage requirements - and they share the
 * support servers and Snowflake Production with Customer Support Data Access,
 * so a combined review has overlapping targets to deduplicate.
 */
export const DEMO_ADVISORY_RECOMMENDATIONS: RecommendationCatalogEntry[] = [
  {
    id: "demo-support-pii-protection",
    demoScenario: "customer-support",
    advisory: true,
    name: "PII Protection",
    outcome:
      "Redact customer-identifying fields in support results while keeping the case context agents need.",
    whyRecommended:
      "Support agents read case records that carry customer contact details. Resolving a ticket needs the case context, not unmasked identifiers.",
    controls: [
      { sense: "ALLOW", label: "Case and ticket fields needed to resolve an inquiry" },
      { sense: "BLOCK", label: "Return unmasked customer-identifying fields" },
    ],
    category: "PRIVACY_PROTECTION",
    technology: "ServiceNow",
    strength: "RECOMMENDED",
    frameworks: ["GDPR"],
    enforcement: {
      resource: "Customer support case records",
      toolGroups: ["Case reads"],
      allowed: ["getCaseHistory", "searchIncidents"],
      blocked: ["Unmasked customer PII"],
      enforcementLayer: "Data-source masking policy",
    },
    selector: {
      purposes: ["CUSTOMER_SUPPORT"],
      technologies: ["ServiceNow", "Salesforce"],
      agentKeys: SUPPORT_AGENT_KEYS,
    },
  },
  {
    id: "demo-support-business-hours",
    demoScenario: "customer-support",
    advisory: true,
    name: "Business Hours Access",
    outcome:
      "Restrict sensitive support access outside staffed business hours.",
    whyRecommended:
      "Support queues are staffed during business hours. Unattended access to case documents and tickets overnight is outside how these agents are expected to run.",
    controls: [
      { sense: "ALLOW", label: "Support access during staffed business hours" },
      { sense: "BLOCK", label: "Sensitive access outside business hours" },
    ],
    category: "BUSINESS_RULES",
    technology: "SharePoint",
    strength: "RECOMMENDED",
    frameworks: ["ISO"],
    enforcement: {
      resource: "Support documents and tickets",
      toolGroups: ["Time-bound access"],
      allowed: ["searchDocuments", "searchIncidents"],
      blocked: ["Access outside 07:00-19:00"],
      enforcementLayer: "MCP server policy",
    },
    selector: {
      purposes: ["CUSTOMER_SUPPORT"],
      technologies: ["SharePoint", "ServiceNow"],
      agentKeys: SUPPORT_AGENT_KEYS.slice(0, 2),
    },
  },
];
